import { Provider } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  RYN_REPLAY_BEARER_PROVIDER,
  RynReplayBearerProvider,
  guardRynReplayBearerProvider,
} from '@ups/xplat/web/core';

import { AuthFacade } from './auth-facade';

/**
 * Bearer for the Ryn Replay ingest mint (#30115; shared by every AuthFacade
 * app since US 37924). The recorder trades this app's own access token for a
 * short-lived session-bound ingest token, so the server, not the browser,
 * decides which user a session belongs to (Bug 37613).
 *
 * Unlike the replay READ token, the mint accepts BOTH issuers, so AuthFacade's
 * token is used whichever one the session is on. AuthFacade is silent-only
 * (Auth0 getAccessTokenSilently, MSAL acquireTokenSilent); the guard turns any
 * failure or empty token into null, which keeps the shared key in use.
 *
 * Inert until the server maps this app's client in
 * RynReplay:IngestTokenClients: an unmapped client gets a 403 from the mint,
 * the client backs off, and every batch still goes out on the key.
 */
export function createRynReplayAuthFacadeBearer(
  auth: Pick<AuthFacade, 'getAccessToken'>
): RynReplayBearerProvider {
  return guardRynReplayBearerProvider(() =>
    firstValueFrom(auth.getAccessToken())
  );
}

/**
 * Opt an app into ingest-token mode with its AuthFacade session. Add it next
 * to `provideRynReplayRrwebBackend()` only in an app that signs users in
 * through AuthModule or the dual-issuer login; an anonymous app has no
 * AuthenticationService for AuthFacade to inject.
 */
export const provideRynReplayAuthFacadeBearer = (): Provider => ({
  provide: RYN_REPLAY_BEARER_PROVIDER,
  useFactory: createRynReplayAuthFacadeBearer,
  deps: [AuthFacade],
});
